import type { ExportJob, FromWorker } from './protocol';

declare global {
  interface Window {
    showSaveFilePicker?: (options?: {
      suggestedName?: string;
      types?: { description?: string; accept: Record<string, string[]> }[];
    }) => Promise<FileSystemFileHandle>;
  }
}

/** Asks where to save. Must be called straight from a click. Null when the browser can't stream to disk. */
export async function pickSaveTarget(suggestedName: string): Promise<FileSystemFileHandle | null | 'aborted'> {
  if (!window.showSaveFilePicker) return null;
  try {
    return await window.showSaveFilePicker({ suggestedName, types: [{ description: 'MP4 video', accept: { 'video/mp4': ['.mp4'] } }] });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return 'aborted';
    return null;
  }
}

export interface ExportHandle {
  cancel: () => void;
  done: Promise<{ bytes: number; blob: Blob | null } | 'canceled'>;
}

export function startExport(job: ExportJob, onMessage: (msg: FromWorker) => void): ExportHandle {
  const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  const done = new Promise<{ bytes: number; blob: Blob | null } | 'canceled'>((resolve, reject) => {
    worker.onmessage = (e: MessageEvent<FromWorker>) => {
      const msg = e.data;
      onMessage(msg);
      if (msg.type === 'done') {
        resolve({ bytes: msg.bytes, blob: msg.buffer ? new Blob([msg.buffer], { type: 'video/mp4' }) : null });
        worker.terminate();
      } else if (msg.type === 'canceled') {
        resolve('canceled');
        worker.terminate();
      } else if (msg.type === 'error') {
        reject(new Error(msg.message));
        worker.terminate();
      }
    };
    worker.onerror = (e) => {
      reject(new Error(e.message || 'The export stopped unexpectedly.'));
      worker.terminate();
    };
  });
  worker.postMessage({ type: 'start', job });
  return { cancel: () => worker.postMessage({ type: 'cancel' }), done };
}

/** Keeps the screen on while exporting. Silently does nothing where not supported. */
export async function holdWakeLock(): Promise<() => void> {
  try {
    const lock = await navigator.wakeLock?.request('screen');
    return () => void lock?.release().catch(() => undefined);
  } catch {
    return () => undefined;
  }
}
